import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as departmentAPI from '../api/departmentAPI';

export interface Department {
  id: number;
  name: string;
  company_id: number;
  head_user_id: number;
  created_at: string;
}

interface DepartmentState {
  data: Department[];
  loading: boolean;
  error: string | null;
}

const initialState: DepartmentState = {
  data: [],
  loading: false,
  error: null
};

// Helper: extract backend error safely
function extractError(err: unknown): string {
  if (typeof err === 'object' && err !== null) {
    const axiosError = err as {
      response?: {
        data?: {
          detail?: string | Array<{ msg: string }>;
        };
      };
      message?: string;
    };

    if (axiosError.response?.data?.detail) {
      const detail = axiosError.response.data.detail;
      if (typeof detail === 'string') {
        return detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        return detail[0].msg || 'Validation error';
      }
    }

    if (axiosError.message) {
      return axiosError.message;
    }
  }
  return 'Unexpected error occurred';
}

// Fetch Departments
export const fetchDepartments = createAsyncThunk<
  Department[],
  departmentAPI.GetDepartmentsParams | undefined,
  { rejectValue: string }
>('departments/fetchDepartments', async (params, { rejectWithValue }) => {
  try {
    const res = await departmentAPI.getDepartments(params);
    return res;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// Create Department
export const addDepartment = createAsyncThunk<
  Department,
  departmentAPI.CreateDepartmentPayload,
  { rejectValue: string }
>('departments/addDepartment', async (departmentData, { rejectWithValue }) => {
  try {
    const res = await departmentAPI.createDepartment(departmentData);
    return res;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// Update Department
export const editDepartment = createAsyncThunk<
  Department,
  { id: number; departmentData: departmentAPI.UpdateDepartmentPayload },
  { rejectValue: string }
>(
  'departments/editDepartment',
  async ({ id, departmentData }, { rejectWithValue }) => {
    try {
      const res = await departmentAPI.updateDepartment(id, departmentData);
      return res;
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);

// Delete Department
export const deleteDepartment = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>('departments/deleteDepartment', async (id, { rejectWithValue }) => {
  try {
    await departmentAPI.deleteDepartment(id);
    return id;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

// Get Company Departments
export const fetchCompanyDepartments = createAsyncThunk<
  Department[],
  number,
  { rejectValue: string }
>(
  'departments/fetchCompanyDepartments',
  async (companyId, { rejectWithValue }) => {
    try {
      const res = await departmentAPI.getCompanyDepartments(companyId);
      return res;
    } catch (err: unknown) {
      return rejectWithValue(extractError(err));
    }
  }
);

const departmentSlice = createSlice({
  name: 'departments',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    resetDepartments: state => {
      state.data = [];
      state.loading = false;
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      // ---------------- Fetch Departments ----------------
      .addCase(fetchDepartments.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDepartments.fulfilled,
        (state, action: PayloadAction<Department[]>) => {
          state.data = action.payload;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch departments';
        state.loading = false;
      })

      // ---------------- Add Department ----------------
      .addCase(addDepartment.pending, state => {
        state.error = null;
      })
      .addCase(
        addDepartment.fulfilled,
        (state, action: PayloadAction<Department>) => {
          state.data.push(action.payload);
          state.error = null;
        }
      )
      .addCase(addDepartment.rejected, (state, action) => {
        state.error = action.payload || 'Failed to create department';
      })

      // ---------------- Edit Department ----------------
      .addCase(editDepartment.pending, state => {
        state.error = null;
      })
      .addCase(
        editDepartment.fulfilled,
        (state, action: PayloadAction<Department>) => {
          const index = state.data.findIndex(d => d.id === action.payload.id);
          if (index !== -1) {
            state.data[index] = action.payload;
          }
          state.error = null;
        }
      )
      .addCase(editDepartment.rejected, (state, action) => {
        state.error = action.payload || 'Failed to update department';
      })

      // ---------------- Delete Department ----------------
      .addCase(deleteDepartment.pending, state => {
        state.error = null;
      })
      .addCase(
        deleteDepartment.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.data = state.data.filter(d => d.id !== action.payload);
          state.error = null;
        }
      )
      .addCase(deleteDepartment.rejected, (state, action) => {
        state.error = action.payload || 'Failed to delete department';
      })

      // ---------------- Fetch Company Departments ----------------
      .addCase(fetchCompanyDepartments.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchCompanyDepartments.fulfilled,
        (state, action: PayloadAction<Department[]>) => {
          state.data = action.payload;
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(fetchCompanyDepartments.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch company departments';
        state.loading = false;
      });
  }
});

export const { clearError, resetDepartments } = departmentSlice.actions;
export default departmentSlice.reducer;
